import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { Bell, CheckCheck, Gift, TrendingUp, FileText, X, AlertCircle } from 'lucide-react';

const TYPE_ICONS = {
  new_proposal:      { icon: FileText,    color: 'text-blue-500',    bg: 'bg-blue-50' },
  proposal_accepted: { icon: TrendingUp,  color: 'text-emerald-500', bg: 'bg-emerald-50' },
  proposal_declined: { icon: X,           color: 'text-red-500',     bg: 'bg-red-50' },
  brief_published:   { icon: Gift,        color: 'text-orange-500',  bg: 'bg-orange-50' },
  default:           { icon: AlertCircle, color: 'text-gray-500',    bg: 'bg-gray-50' },
};

const timeAgo = (iso) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const NotificationBell = ({ accentColor = 'teal' }) => {
  const { notifications, unread, markAllRead, markRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const badgeColor = accentColor === 'orange' ? 'bg-orange-500' : accentColor === 'green' ? 'bg-green-500' : accentColor === 'purple' ? 'bg-purple-500' : 'bg-teal-500';
  const ringColor  = accentColor === 'orange' ? 'ring-orange-500' : accentColor === 'green' ? 'ring-green-500' : accentColor === 'purple' ? 'ring-purple-500' : 'ring-teal-500';

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen(o => !o)}
        className={`relative p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors ${open ? `ring-2 ${ringColor} ring-offset-1 bg-gray-100` : ''}`}
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] ${badgeColor} text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1`}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <p className="font-bold text-gray-900 text-sm">Notifications</p>
              {unread > 0 && (
                <span className={`text-xs ${badgeColor} text-white font-bold px-2 py-0.5 rounded-full`}>{unread} new</span>
              )}
            </div>
            {unread > 0 && (
              <button onClick={markAllRead} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors">
                <CheckCheck className="w-3.5 h-3.5" />Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-10 text-center text-gray-400">
                <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : notifications.slice(0, 15).map(n => {
              const cfg = TYPE_ICONS[n.type] || TYPE_ICONS.default;
              const Icon = cfg.icon;
              return (
                <div
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  onClick={() => !n.read && markRead(n.id)}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50 transition-colors ${!n.read ? 'bg-blue-50/40' : ''}`}
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                    <Icon className={`w-4 h-4 ${cfg.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm leading-tight ${!n.read ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                        {n.title}
                      </p>
                      <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">{timeAgo(n.created_at)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed line-clamp-2">{n.body}</p>
                  </div>
                  {!n.read && <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1" />}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-100 text-center">
              <button onClick={() => setOpen(false)} className="text-xs text-gray-400 hover:text-gray-600">
                Close
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
